import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { toast } from "react-toastify";
import {
  fetchAdminByEmail,
  handleRoutePartnerAdmin,
  handleRegularAdmin,
} from "../utils/authUtils";

const EXCLUDING_PATHS = ["/workstation", "/shipping/quote", "/unsubscribe"];

/**
 * Custom hook to handle authentication state and user management
 * @returns {void}
 */
export const useAuth = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const auth = getAuth();
  const unsubscribeRef = useRef(null);
  const routePartnerUnsubscribeRef = useRef(null);

  useEffect(() => {
    // Subscribe to auth state changes
    unsubscribeRef.current = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Clean up previous route partner listener if exists
          if (routePartnerUnsubscribeRef.current) {
            routePartnerUnsubscribeRef.current();
            routePartnerUnsubscribeRef.current = null;
          }

          await handleAuthenticatedUser(
            user,
            dispatch,
            routePartnerUnsubscribeRef
          );
        } catch (error) {
          console.error("Error handling authenticated user:", error);
          toast.error("An error occurred while processing your authentication.");
        }
      } else {
        handleUnauthenticatedUser(location, navigate);
      }
    });

    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (routePartnerUnsubscribeRef.current) {
        routePartnerUnsubscribeRef.current();
      }
    };
  }, [auth, dispatch, location, navigate]);
};

/**
 * Handles authenticated user flow
 * @param {Object} user - Firebase user object
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} routePartnerUnsubscribeRef - Ref to store route partner unsubscribe function
 */
const handleAuthenticatedUser = async (user, dispatch, routePartnerUnsubscribeRef) => {
  console.log(user.email, "user in useeffect");

  // Fetch admin data
  const admins = await fetchAdminByEmail();
  const userWithRole = admins.find((admin) => admin.email === user.email);

  if (!userWithRole) {
    toast.error("Admin User not found.");
    return;
  }

  // Handle different admin roles
  if (userWithRole?.role === "Route Partner Admin") {
    const unsubscribe = handleRoutePartnerAdmin(
      user,
      userWithRole,
      dispatch
    );
    routePartnerUnsubscribeRef.current = unsubscribe;
  } else {
    handleRegularAdmin(user, userWithRole, dispatch);
  }

  // Store token
  localStorage.setItem("token", user.accessToken);
};

/**
 * Handles unauthenticated user flow
 * @param {Object} location - React Router location object
 * @param {Function} navigate - React Router navigate function
 */
const handleUnauthenticatedUser = (location, navigate) => {
  console.log(location, "location");
  if (!EXCLUDING_PATHS.includes(location?.pathname)) {
    navigate("/");
  }
};

